/**
 * Reconcile the project-local plugin store (`<projectDir>/.skaile/plugins/`).
 *
 * The reconciler is hash-skipped: it computes a SHA-256 over the sorted plugin
 * list plus the lock file's `plugins` slice and compares it to the stored
 * `.reconcile-hash`. On a match it returns with zero further I/O; otherwise it
 * regenerates the store's `package.json` and runs `bun install` against it.
 *
 * The install step is injectable (`InstallFn`) so tests stay hermetic — they
 * pass a fake installer that records calls and writes a stub `node_modules`.
 */

import { createHash } from "node:crypto";
import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { readPluginsLockSlice } from "@skaile/workspaces/core";
import { pluginsDir, pluginsPkgJson, reconcileHashFile } from "./paths.js";
import { parseSpec } from "./spec.js";

/**
 * Installs the store's dependencies. Returns `ok: false` (never throws) on a
 * failed install so the reconciler can surface the captured `output`.
 */
export type InstallFn = (pluginsDir: string) => Promise<{ ok: boolean; output: string }>;

/** Options for {@link reconcilePlugins}. */
export interface ReconcileOptions {
  /** Injectable installer. Defaults to a real `bun install` of the store. */
  runInstall?: InstallFn;
  /**
   * Lock slice to fold into the reconcile-hash. Defaults to
   * `readPluginsLockSlice(projectDir)` so a lock edit invalidates the hash.
   */
  lockSlice?: unknown;
}

/** Result of a reconcile pass. */
export interface ReconcileResult {
  changed: boolean;
  reason: "no-plugins" | "up-to-date" | "reconciled";
}

/**
 * The default real installer: `bun install` inside the store directory.
 * Frozen on subsequent runs (a `bun.lock`/`bun.lockb` already exists), and
 * non-frozen on the first run so a lock can be generated.
 */
export const defaultInstall: InstallFn = async (dir) => {
  const { portableSpawn } = await import("@skaile/workspaces/core");
  const hasLock =
    existsSync(`${dir}/bun.lock`) ||
    existsSync(`${dir}/bun.lockb`) ||
    existsSync(`${dir}/package-lock.json`);
  const args = hasLock ? ["bun", "install", "--frozen-lockfile"] : ["bun", "install"];
  const proc = portableSpawn(args, { cwd: dir });
  const chunks: string[] = [];
  const decoder = new TextDecoder();
  const pump = async (stream: ReadableStream<Uint8Array>) => {
    const reader = stream.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) chunks.push(decoder.decode(value, { stream: true }));
    }
  };
  await Promise.all([pump(proc.stdout), pump(proc.stderr)]);
  const code = await proc.exited;
  return { ok: code === 0, output: chunks.join("") };
};

/** Generate the store's `package.json` from the plugin specs. */
function writePackageJson(projectDir: string, plugins: string[]): void {
  const dependencies: Record<string, string> = {};
  for (const spec of plugins) {
    const { name, range } = parseSpec(spec);
    dependencies[name] = range;
  }
  const pkg = {
    name: "skaile-project-plugins",
    private: true,
    dependencies,
  };
  mkdirSync(pluginsDir(projectDir), { recursive: true });
  writeFileSync(pluginsPkgJson(projectDir), `${JSON.stringify(pkg, null, 2)}\n`);
}

/**
 * Reconcile the plugin store for a project.
 *
 * @param projectDir - Project root containing `skaile.yaml` / `skaile.lock.yaml`.
 * @param plugins - The `skaile.yaml` `plugins:` list (npm specifiers).
 * @param opts - Optional injectable installer + lock slice override.
 * @returns Whether the store changed and why.
 */
export async function reconcilePlugins(
  projectDir: string,
  plugins: string[],
  opts: ReconcileOptions = {},
): Promise<ReconcileResult> {
  if (plugins.length === 0) {
    return { changed: false, reason: "no-plugins" };
  }

  const lockSlice = opts.lockSlice ?? readPluginsLockSlice(projectDir);
  const wantHash = createHash("sha256")
    .update(JSON.stringify({ plugins: [...plugins].sort(), lock: lockSlice }))
    .digest("hex");

  const hashFile = reconcileHashFile(projectDir);
  if (existsSync(hashFile)) {
    const have = readFileSync(hashFile, "utf8").trim();
    if (have === wantHash) {
      return { changed: false, reason: "up-to-date" };
    }
  }

  writePackageJson(projectDir, plugins);

  const install = opts.runInstall ?? defaultInstall;
  const result = await install(pluginsDir(projectDir));
  if (!result.ok) {
    throw new Error(`plugin install failed:\n${result.output}`);
  }

  writeFileSync(hashFile, wantHash);
  return { changed: true, reason: "reconciled" };
}
